import { useState } from 'react';
import {
  X,
  Sparkles,
  Loader2,
  FileText,
} from 'lucide-react';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  selectedText: string;
  onRegenerate: (instructions: string) => Promise<void>;
}

export default function SelectiveRegenerateModal({ isOpen, onClose, selectedText, onRegenerate }: Props) {
  const [instructions, setInstructions] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  if (!isOpen) return null;

  const truncatedText =
    selectedText.length > 500
      ? selectedText.substring(0, 500) + '...'
      : selectedText;

  const handleRegenerate = async () => {
    if (isLoading) return;
    setIsLoading(true);
    try {
      await onRegenerate(instructions);
      setInstructions('');
      onClose();
    } catch {
      // Error handling done by parent
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      handleRegenerate();
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content max-w-lg" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <div className="flex items-center gap-2">
            <Sparkles size={20} className="text-accent" />
            <h2 className="text-lg font-semibold">Regenerate Selection</h2>
          </div>
          <button
            onClick={onClose}
            disabled={isLoading}
            className="p-1.5 hover:bg-gray-100 rounded disabled:opacity-50"
          >
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="p-5 space-y-4">
          {/* Selected Text Preview */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              <div className="flex items-center gap-1.5">
                <FileText size={14} />
                Selected Text
              </div>
            </label>
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 max-h-40 overflow-y-auto">
              <p className="text-sm text-gray-600 whitespace-pre-wrap break-words leading-relaxed">
                {truncatedText}
              </p>
              {selectedText.length > 500 && (
                <p className="text-xs text-gray-400 mt-2 italic">
                  Showing first 500 of {selectedText.length} characters
                </p>
              )}
            </div>
          </div>

          {/* Instructions */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              Revision Instructions
            </label>
            <textarea
              value={instructions}
              onChange={(e) => setInstructions(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Describe how you'd like this text to be revised... (e.g., 'Make it more formal', 'Add more detail about...', 'Simplify the language')"
              rows={4}
              className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-accent/30 focus:border-accent resize-none"
              disabled={isLoading}
              autoFocus
            />
            <p className="text-xs text-gray-400 mt-1">
              Press Ctrl+Enter to regenerate
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-2 p-4 border-t border-gray-200">
          <button
            onClick={onClose}
            disabled={isLoading}
            className="btn btn-secondary"
          >
            Cancel
          </button>
          <button
            onClick={handleRegenerate}
            disabled={isLoading}
            className="btn btn-primary flex items-center gap-2"
          >
            {isLoading ? (
              <>
                <Loader2 size={16} className="animate-spin" />
                Regenerating...
              </>
            ) : (
              <>
                <Sparkles size={16} />
                Regenerate
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
